/**
 * EnergyBadge — compose-time energy balance pill.
 *
 * Spec: docs/plans/energy-visibility-ux.md §1. New users saw their 150-energy
 * grant nowhere (balance was only visible as the atCap paywall), which maps
 * to the observed 0/3 register→first-card activation. This pill makes the
 * grant visible at the moment of spending.
 *
 * - Presentational, no props: reads usePayment() directly (like SettingsView).
 * - Non-interactive (span, not button): information, not a CTA. The atCap
 *   paywall card below remains the only top-up surface (activation-first).
 * - role="status" → screen readers announce balance changes (optimistic debit,
 *   post-checkout top-up) on re-render.
 * - One-shot pulse when the balance decreases; skipped under
 *   prefers-reduced-motion (decorative enhancement only).
 */

import { useEffect, useRef, useState } from 'react'
import { Zap } from 'lucide-react'
import { usePayment } from '@/hooks/usePayment'
import { useT } from '@/contexts/I18nContext'

/** How long the debit pulse stays on (~one heartbeat). */
const PULSE_MS = 600

export function EnergyBadge() {
  const t = useT()
  const { status, isLoading } = usePayment()
  const balance = status?.balanceCents ?? 0
  const isZero = !!status && balance === 0

  // One-shot pulse on balance decrease (optimistic debit at generate time).
  // Skipped under prefers-reduced-motion; jsdom/tests lack matchMedia, hence
  // the typeof guard.
  const prevBalance = useRef<number | null>(null)
  const [pulse, setPulse] = useState(false)
  useEffect(() => {
    const decreased = prevBalance.current !== null && prevBalance.current > balance
    prevBalance.current = balance
    if (!decreased) return
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) return
    // Decorative one-shot: the pulse must START the frame the debit lands,
    // which rules out the timeout-callback pattern. The compiler lint
    // prefers render-phase state, but that requires ref access during
    // render (also banned) — this disable is the deliberate trade-off.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPulse(true)
  }, [balance])

  // Reset timer is armed purely off `pulse`, NOT off `balance`: if the
  // balance decreases then increases again within PULSE_MS, keying the
  // timer on `balance` would clear it in the first effect's cleanup and
  // the pulse would never reset. Keying on `pulse` guarantees exactly one
  // live reset timer whenever the pulse is on.
  useEffect(() => {
    if (!pulse) return
    const timer = setTimeout(() => setPulse(false), PULSE_MS)
    return () => clearTimeout(timer)
  }, [pulse])

  // PaymentContext initial fetch — skeleton dash, never a flashing "0".
  const loading = isLoading && !status

  return (
    <span
      className={`shrink-0 flex items-center gap-1 px-3 py-1.5 text-[12px] font-bold tracking-[0.05em] rounded-full bg-surface-soft ${
        isZero ? 'text-on-surface-variant/60' : 'text-on-surface-variant'
      }${pulse ? ' animate-pulse' : ''}`}
      aria-label={loading ? undefined : isZero ? t('generate.energyBadge.zero') : t('generate.energyBadge.label', { balance })}
      role="status"
      aria-busy={loading ? true : undefined}
    >
      <Zap className="w-[14px] h-[14px] text-primary" strokeWidth={2} aria-hidden="true" />
      {loading ? '–' : balance}
    </span>
  )
}
